import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  getClearingExportBatches,
  ZenginExportBatch,
} from '~/javascripts/admin/actions/clearing';
import Headings from '../../atoms/Headings';
import BreadCrumb from '../../molecules/BreadCrumb';

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '精算管理', href: '', isCurrentPage: true },
  { value: '振込データ履歴', href: '', isCurrentPage: true },
];

const PageWrapper = styled.div`
  padding-bottom: 40px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #e5e5e5;
    text-align: left;
    vertical-align: top;
  }
  th {
    background: #fafafa;
    white-space: nowrap;
  }
  td.amount {
    text-align: right;
  }
`;

const Badge = styled.span<{ tone: 'warning' | 'done' | 'muted' }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  white-space: nowrap;
  ${({ tone }) => {
    if (tone === 'warning') return 'background: #fff3cd; color: #8a6d3b;';
    if (tone === 'done') return 'background: #e6f4ea; color: #1e7e34;';
    return 'background: #eee; color: #777;';
  }}
`;

const Empty = styled.div`
  padding: 32px;
  text-align: center;
  color: #888;
`;

const Note = styled.p`
  color: #666;
  font-size: 13px;
`;

const yen = (amount: number) => `${amount.toLocaleString()} 円`;

// バッチの状態。未精算が残っているものを目立たせる（振込したのに精算し忘れている可能性）。
const statusOf = (batch: ZenginExportBatch) => {
  if (batch.discarded_at) return { tone: 'muted' as const, label: '破棄済み' };
  if ((batch.unsettled_count ?? 0) > 0) {
    return { tone: 'warning' as const, label: `未精算 ${batch.unsettled_count} 件` };
  }
  return { tone: 'done' as const, label: '精算済み' };
};

// サーバ側の一覧上限。これを超える分は返らないので、黙って切り捨てずに件数で伝える。
const FETCH_LIMIT = 200;

const ClearingExportBatches = () => {
  const [batches, setBatches] = useState<ZenginExportBatch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const fetchBatches = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const result = await getClearingExportBatches(`limit=${FETCH_LIMIT}`);
      setBatches(result.export_batches ?? []);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  return (
    <PageWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      <Headings.h5>振込データ履歴</Headings.h5>
      <Note>
        ダウンロードした振込データの一覧です。銀行へのアップロードが済んだら、対象のデータを開いて
        「精算済にする」を実行してください。銀行に上げなかったデータは「破棄」しておくと、
        未精算の警告に出てこなくなります。
      </Note>

      {isLoading && <Empty>読み込み中...</Empty>}

      {!isLoading && hasError && <Empty>振込データを取得できませんでした</Empty>}

      {batches.length >= FETCH_LIMIT && (
        <Note>直近 {FETCH_LIMIT} 件を表示しています。これより古い振込データは表示されません。</Note>
      )}

      {!isLoading && !hasError && batches.length === 0 && (
        <Empty>振込データはまだありません。精算データ一覧から作成できます。</Empty>
      )}

      {!isLoading && !hasError && batches.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th>作成日時</th>
              <th>振込指定日</th>
              <th>件数</th>
              <th>金額</th>
              <th>状態</th>
              <th>作成者</th>
              <th aria-label="操作" />
            </tr>
          </thead>
          <tbody>
            {batches.map((batch) => {
              const status = statusOf(batch);
              return (
                <tr key={batch.id}>
                  <td>{moment(batch.created_at).format('YYYY/MM/DD HH:mm')}</td>
                  <td>{moment(batch.scheduled_transfer_date).format('YYYY/MM/DD')}</td>
                  <td className="amount">{batch.transfer_count} 件</td>
                  <td className="amount">{yen(batch.total_amount)}</td>
                  <td>
                    <Badge tone={status.tone}>{status.label}</Badge>
                  </td>
                  <td>{batch.operator?.email}</td>
                  <td>
                    <Link to={`/clearings/export-batches/${batch.id}`}>詳細</Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </PageWrapper>
  );
};

export default ClearingExportBatches;
